import React, { useState } from 'react'
import { useGetStatesQuery } from 'redux/services/states'
import { FieldErrors, useFormContext } from 'react-hook-form'

import styled from '@emotion/styled'
import { DEFAULT_THEME_COLORS } from 'css/theme'

import Label from 'modules/ui/atoms/Typography/Label'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import { UiIcon } from 'modules/ui/atoms/Icon'

import { STRIPE_COUNTRY_CODES } from 'utils/constants/misc'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'

import { type FormData } from 'modules/flow/components/donor-information-step/donor-information-step'
import { useInputEventDebounce } from 'utils/hooks/useInputEventDebounce'
import { useGoogleAutoComplete } from './utils'
import { useTranslation } from 'next-i18next'

interface OrderAddressProps {
  errors: FieldErrors<FormData>
  watchCountry: string
  watchState: string
}

// id names are very important for both autofill & autocomplete to work as expected
const FIELD_IDS = {
  country: 'country',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  state: 'state',
  zipCode: 'zipCode',
}

export default function OrderAddress({ errors, watchCountry, watchState }: OrderAddressProps) {
  const enableLine2 = true
  const [showLine2Input, setShowLine2Input] = React.useState(false)
  const [autoComplete, setAutoComplete] = useState('')

  const form = useFormContext()
  const { t } = useTranslation()

  const handleAddLine2Click = () => {
    setShowLine2Input(true)
  }

  const handleAddLine2KeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      setShowLine2Input(true)
    }
  }

  // This is a workaround to prevent the form from submitting when the user presses the Enter key
  // while the Places Autocomplete widget is open.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  const handleOnFocus = () => {
    // This is a workaround to prevent the browser from auto-filling the address field
    // when the user selects an address from the Places Autocomplete widget.
    // Chrome won't accept the 'off' value, so we use a non-standard value instead.
    setAutoComplete('non-standard-value')
  }

  const handleOnBlur = () => {
    // When the field loses focus, we reset the autoComplete attribute to its default value.
    // This allows the browser to auto-fill the address field when the user selects an address
    // through the other fields in the form.
    setAutoComplete('')
  }

  const countries = React.useMemo(() => {
    return STRIPE_COUNTRY_CODES.map(({ country, code }) => ({ value: code, label: country }))
  }, [])

  const { data: states, isFetching: statesIsFetching } = useGetStatesQuery({
    countryCode: watchCountry,
  })

  // Debounces calls to the Google Places API.
  useInputEventDebounce(FIELD_IDS.addressLine1, { ms: 400 })

  const { ref } = useGoogleAutoComplete({
    enableLine2: enableLine2,
    setShowLine2Input: (bool) => setShowLine2Input(bool || false),
    setAddress1: (line1) => form.setValue(FIELD_IDS.addressLine1, line1),
    setAddress2: FIELD_IDS.addressLine2
      ? (line2) => form.setValue(FIELD_IDS.addressLine2, line2)
      : null,
    setCity: (city) => form.setValue(FIELD_IDS.city, city),
    setState: (state) => form.setValue(FIELD_IDS.state, state),
    setCountry: (country) => form.setValue(FIELD_IDS.country, country),
    setZipcode: (zipCode) => form.setValue(FIELD_IDS.zipCode, zipCode),
    triggerValidation: () =>
      Object.values(FIELD_IDS).forEach((id) => {
        form.trigger(id)
      }),
  })

  const StateField = () => {
    let field = null

    if (statesIsFetching) {
      const StyledStateField = styled.div`
        margin-top: 12px;
      `

      field = (
        <StyledStateField>
          <Label size="sm">{t('OrderAddress.label.stateRequired', 'State *')}</Label>
          <Placeloader
            style={{ minHeight: '46px', borderRadius: '8px', margin: '4px 0', width: '100%' }}
          />
        </StyledStateField>
      )
    } else {
      const hasStates = (states?.length ?? 0) > 0

      let label = ''

      switch (watchCountry) {
        case 'US':
          label = t('OrderAddress.label.state', 'State')
          break
        case 'CA':
          label = t('OrderAddress.label.province', 'Province')
          break
        default:
          label = t('OrderAddress.label.region', 'Region')
      }

      // Maps the states to the options for the select field
      const options = states?.map((state) => ({ value: state.isoCode, label: state.name })) ?? []
      options.unshift({ value: '', label: t('OrderAddress.label.pleaseSelect', 'Please select') })

      field = (
        <SelectField
          required={hasStates}
          id={FIELD_IDS.state}
          label={label}
          error={errors.state as { message: string }}
          options={options}
          autoComplete="address-level1"
          dataId="address-state-select"
          value={watchState}
        />
      )
    }

    return field
  }

  return (
    <Column>
      <Row>
        <Column>
          <InputField
            required
            id={FIELD_IDS.addressLine1}
            label={t('OrderAddress.label.address', 'Address')}
            error={errors.addressLine1 as { message: string }}
            onKeyDown={handleKeyDown}
            inputRef={ref}
            overwriteValueWithFocus={true}
            autoComplete={autoComplete}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            placeholder={t('AddressFields.address.placeholder', 'Enter a location')}
            dataId="address-line1-input"
          />

          {!showLine2Input && (
            <AddLine2Container
              role="button"
              tabIndex={0}
              data-id="address-line2-toggle"
              onClick={handleAddLine2Click}
              onKeyDown={handleAddLine2KeyPress}
            >
              <UiIcon
                w={22}
                h={22}
                name="add_circle"
                color={DEFAULT_THEME_COLORS['--theme-color--brand-primary']}
              />

              <Label size="sm" color={DEFAULT_THEME_COLORS['--theme-color--brand-primary']}>
                {t('OrderAddress.label.addLine2Prompt', 'Add apt., suite, floor')}
              </Label>
            </AddLine2Container>
          )}

          {showLine2Input && (
            <Row>
              <Column style={{ flex: 1 }}>
                <InputField
                  id={FIELD_IDS.addressLine2}
                  label={t('OrderAddress.label.address2Optional', 'Address 2 (optional)')}
                  placeholder={t('OrderAddress.placeholder.address2', 'Apt., suite, floor')}
                  error={errors.addressLine2 as { message: string }}
                  autoComplete="address-line2"
                  dataId="address-line2-input"
                />
              </Column>
            </Row>
          )}
        </Column>
      </Row>

      <Row>
        <Column>
          <InputField
            required
            id={FIELD_IDS.zipCode}
            dataId="address-zipcode-input"
            error={errors.zipCode as { message: string }}
            label={
              watchCountry == 'US'
                ? t('OrderAddress.label.zipCode', 'Zip Code')
                : t('OrderAddress.label.postalCode', 'Postal Code')
            }
            autoComplete="postal-code"
          />
        </Column>
        <Column>
          <InputField
            required
            id={FIELD_IDS.city}
            label={t('OrderAddress.label.city', 'City')}
            dataId="address-city-input"
            error={errors.city as { message: string }}
            autoComplete="address-level2"
          />
        </Column>
      </Row>

      <Row>
        <Column>{StateField()}</Column>
        <Column>
          <SelectField
            required
            id={FIELD_IDS.country}
            label={t('OrderAddress.label.country', 'Country')}
            dataId="address-country-input"
            error={errors.country as { message: string }}
            options={countries}
            autoComplete="country"
            value={watchCountry}
          />
        </Column>
      </Row>
    </Column>
  )
}

const AddLine2Container = styled(Row)`
  cursor: pointer;
  width: fit-content;
  align-items: center;
  gap: 2px;
`
